import { components } from '../../Components';
import { BaseComponent } from '../../Core/BaseComponent/BaseComponent';
import Dropzone, { Accept } from 'react-dropzone';
import { SerendieSymbol } from '@serendie/symbols';
import "./Styles.scss"
import { GridContainer } from '../../Layout/Grid/GridContainer';
import { GridItem } from '../../Layout/Grid/GridItem';
import { Button } from '../../Buttons/Button/Button';
import { JSX } from 'react';

export class FileDropZone extends BaseComponent<IFileDropZoneProps, IFileDropZoneState>
{
    public static prompt = '';
    public static selectButtonLabel = '';
    public static resetButtonLabel = '';

    constructor(props: IFileDropZoneProps)
    {
        super(props);
        this.state = {
            filesToPreview: []
        };
    }

    public componentDidUpdate(prevProps: Readonly<IFileDropZoneProps>): void 
    {
        if (this.props.resetFilesToken !== prevProps.resetFilesToken)
        {
            this.resetFilesToPreview();
        }
    }

    private getNextAvailableName(base: string, existing: string[]): string
    {
        const escapedBase = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const versionPattern = new RegExp(`^${escapedBase}(?:\\((\\d+)\\))?$`);

        let max = 0;

        for (const name of existing)
        {
            const match = name.match(versionPattern);
            if (match)
            {
                const num = match[1] ? parseInt(match[1], 10) : 0;
                max = Math.max(max, num + 1);
            }
        }

        return max === 0 ? base : `${base}(${max})`;
    }

    private async handleFileDrop(files: File[]): Promise<void>
    {
        this.setState({ filesToPreview: [] });
        let filesToPreview = [] as File[];
        if (this.props.previewFileOnDrop === true && this.props.onFilesDropped)
        {
            files.forEach(file =>
            {
                filesToPreview = [...filesToPreview, file]
            });
            this.props.onFilesDropped(filesToPreview.length > 0 ? filesToPreview[0] : null);
            this.setState({ filesToPreview: filesToPreview });
        }
        else
        {
            this.uploadFiles(files)
        }
    }

    private async uploadFiles(files: File[]): Promise<void>
    {
        try
        {
            let i = 0;
            while (i < files.length)
            {
                let filename = files[i].name;
                const fileType = filename.split('.').pop() ?? '';

                if (this.props.existingFileNames.includes(files[i].name))
                {
                    filename = this.getNextAvailableName(filename.split('.')[0], this.props.existingFileNames.map(x => x.split('.')[0])) + `.${fileType}`;
                }

                const formData = new FormData();
                formData.append("file", files[i]);
                formData.append("fileName", filename);
                await this.handleFileUpload(this.props.uploadPath + filename, formData);
                i = i + 1;
            }
            if (this.props.onFilesUploaded)
            {
                this.props.onFilesUploaded()
            }
        }
        catch (error)
        {
            console.error(error)
        }

    }

    private async handleFileUpload(path: string, formData: FormData): Promise<void>
    {
        await components.webEntryApiClientV1.api.webentry.v1.file.byPath.post({
            path: path,
            processAsMapFile: false,
            body: formData
        });
    }

    private resetFilesToPreview(): void
    {
        this.setState({ filesToPreview: [] });
        if (this.props.onFilesDropped)
        {
            this.props.onFilesDropped(null);
        }
    }

    public render(): JSX.Element
    {
        const previewSelectionMode = this.props.previewFileOnDrop && this.state.filesToPreview.length > 0;
        return (
            <Dropzone maxFiles={this.props.maxFiles} multiple={this.props.maxFiles !== undefined && this.props.maxFiles > 1} accept={this.props.allowedFileTypes} onDrop={(acceptedFiles, fileRejections, event) => this.handleFileDrop(acceptedFiles)}>
                {({ getRootProps, getInputProps, isDragActive }) => (
                    <section>
                        <div
                            className="dropzone__area"
                            style={{
                                border: `1px dotted ${isDragActive ? '#2E7BB9' : '#7A7B82'}`,
                                background: previewSelectionMode ? '#F0EEE9' : '',
                            }}
                            {...getRootProps()}
                        >
                            <GridContainer>
                                <GridItem>
                                    <input {...getInputProps()} />
                                </GridItem>
                                <GridItem >
                                    <div className="dropzone__icon-wrapper">
                                        <div className="dropzone__icon-circle">
                                            <SerendieSymbol name="upload-cloud" />
                                        </div>
                                    </div>
                                </GridItem>
                                {
                                    previewSelectionMode ?
                                        <>
                                            <GridItem>
                                                {this.state.filesToPreview[0].name}
                                            </GridItem>
                                            <GridItem>
                                                <Button
                                                    styleType='ghost'
                                                    onClick={() => this.resetFilesToPreview()}
                                                    leftIcon={<SerendieSymbol name='refresh' />}
                                                >
                                                    {this.props.resetButtonLabel ?? FileDropZone.resetButtonLabel}
                                                </Button>
                                            </GridItem>
                                        </>
                                        :
                                        <>
                                            <GridItem>
                                                {this.props.prompt ?? FileDropZone.prompt}
                                            </GridItem>
                                            <GridItem>
                                                <Button
                                                    styleType='ghost'
                                                >
                                                    {this.props.selectButtonLabel ?? FileDropZone.selectButtonLabel}
                                                </Button>
                                            </GridItem>
                                        </>
                                }
                            </GridContainer>
                        </div>
                    </section>
                )}
            </Dropzone>
        );
    }
}

export interface IFileDropZoneProps
{
    prompt?: string;
    selectButtonLabel?: string;
    resetButtonLabel?: string;
    maxFiles?: number;
    uploadPath?: string;
    allowedFileTypes: Accept;
    onFilesUploaded?: () => void;
    existingFileNames: string[];
    previewFileOnDrop?: boolean;
    onFilesDropped?: (file: File | null) => void;
    resetFilesToken: string;
}

export interface IFileDropZoneState
{
    filesToPreview: File[];
}